import { FunctionComponent, ReactNode, useCallback, useEffect, useId, useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"
import { styles } from "../../styles/themes/style";
import ReactSelect, { Props, StylesConfig, components as component } from "react-select";
import { ChevronDown } from "lucide-react";
import { closeOtherOpenDropdowns, registerOpenDropdown, unregisterOpenDropdown } from "../dropdown/openDropdownRegistry";

interface IStyledSelectProps extends Props {
    $components?: boolean
    maxHeight?: number,
    isMulti?: boolean,
    menuIsOpen?: boolean,
    menuIsRelative?: boolean,
    isClearable?: boolean,
    isMultiRemove?: boolean
    prefixIcon?: ReactNode
    fieldLabel?: string
    /** highlights the selected value (used by the filter sidebar to mark an applied filter) */
    fieldActive?: boolean
    onInputChange?: (e: any, actions: any) => void
    handleMultiValueRemove?: (e: any, props: any) => void
}

 /**
  * Use Select as the form control, search select, mutli select etc.
  */
const Select: FunctionComponent<IStyledSelectProps> = ({ $components, maxHeight, isMulti, menuIsOpen, menuIsRelative, isMultiRemove, isClearable, prefixIcon, fieldLabel, fieldActive, onInputChange, handleMultiValueRemove, onMenuOpen, onMenuClose, components: userComponents, ...props }) => {
    const theme: DefaultTheme = useTheme();
    const selectId = useId();
    // when the caller drives the menu we stay out of the way
    const isMenuControlled = menuIsOpen !== undefined;
    const [internalMenuOpen, setInternalMenuOpen] = useState(false);

    // stop tracking a menu that was closed from the outside (or unmounted)
    useEffect(() => {
        if (!internalMenuOpen) unregisterOpenDropdown(selectId);
    }, [internalMenuOpen, selectId]);

    useEffect(() => () => unregisterOpenDropdown(selectId), [selectId]);

    const openMenuHandler = useCallback(() => {
        if (!isMenuControlled) {
            // the focused element sits inside this select, so panels wrapping it stay open
            closeOtherOpenDropdowns(selectId, document.activeElement);
            registerOpenDropdown(selectId, { element: null, close: () => setInternalMenuOpen(false) });
            setInternalMenuOpen(true);
        }
        onMenuOpen?.();
    }, [isMenuControlled, selectId, onMenuOpen]);

    const closeMenuHandler = useCallback(() => {
        if (!isMenuControlled) {
            unregisterOpenDropdown(selectId);
            setInternalMenuOpen(false);
        }
        onMenuClose?.();
    }, [isMenuControlled, selectId, onMenuClose]);
    //has value can be sent, so it is better resolved
    let customStyles: StylesConfig = {
        control: (base, props) => {
            return {
                 ...base,
                display: 'flex',
                alignItems: 'center',
                height: isMulti ? 'auto' : 34,
                minHeight: 34,
                boxSizing: 'border-box',
                padding: '0',
                // border: `2px solid ${props.hasValue ? styles.colors.primary600 : theme.quinaryColor}`, TODO: This should be implemented better, it is always primary600
                border: `2px solid ${theme.quinaryColor}`,
                backgroundColor: props.selectProps.isDisabled ? styles.colors.black100 : '',
                cursor: props.selectProps.isDisabled ? 'default' : 'pointer',
                boxShadow: 'none',

                '& div p': {
                    margin: '0',
                    height: '24px'
                },
                '&:hover': {
                    boxShadow: 'none',
                    border: `2px solid ${props.selectProps.isDisabled ? styles.colors.black200 : styles.colors.primary600}`
                },
                '&:active': {
                    boxShadow: 'none',
                    border: `2px solid ${props.selectProps.isDisabled ? styles.colors.black200 : styles.colors.primary600}`
                }
            }
        },
        container: () => ({
            width: '100%',
            display: 'inline-block',
            ...(theme.primary ? {} : { border: `2px solid ${styles.colors.darkblack500}` }),

        }),
        menu: (base, props) => {
            return {
                backgroundColor: theme.secondaryColor,
                borderRadius: '3px',
                marginTop: '5px',
                padding: 0,
                boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.1)',
                position: menuIsRelative ? 'relative' : 'absolute',
                width: '100%',
                zIndex: 1004,
                cursor: props.selectProps.isDisabled ? 'none' : 'pointer',
                ...maxHeight && {
                    maxHeight: `${maxHeight}px`,
                    overflowY: 'auto',
                }
            }
        },
        menuList: (base, props) => {
            return {
                ...base,
                height: '100%'
            }
        },
        menuPortal: (base) => ({
            ...base,
            zIndex: 1000000,
        }),
        dropdownIndicator: (base, props) => {
            return {
                ...base,
                padding: '0px',
                color: styles.colors.black300,
                transition: 'all .2s ease',
                transform: props.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
            }
        },
        clearIndicator: (base: any) => ({
            ...base,
            padding: '0 0px',
            width: 'auto'
        }),
        valueContainer: (base, props) => {
            return {
                height: '30px',
                padding: '0 0 0 6px',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1
            }
        },
        indicatorsContainer: (base, props) => {
            return {
                ...base,
                alignSelf: 'center',
                height: '20px',
                padding: '0px',
                ...(props.selectProps.isClearable ? {} : { width: '20px' }),
                // marginRight: '6px'
            }
        },
        option: (base, props) => {
            return {
                textAlign: 'left',
                fontFamily: 'NunitoSans',
                fontSize: '14px',
                fontWeight: '400',
                color: theme.primaryColor,
                padding: '6px 20px',
                cursor: 'pointer',
    
                '&:hover': {
                    color: styles.colors.primary600,
                    backgroundColor: theme.senaryColor,
                }
            }
        },
        placeholder: (base, props) => ({
            ...base,
            ...(props.selectProps.isSearchable ? {
                position: 'absolute',
                left: '6px',
                right: '24px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                margin: 0,
            } : {}),
            lineHeight: '30px',
            fontFamily: 'NunitoSans',
            fontSize: '14px',
            color: styles.colors.black300
        }),
        input: (base, props) => ({
            ...base,
            ...(props.selectProps.isSearchable ? {
                margin: 0,
                paddingTop: 0,
                paddingBottom: 0,
            } : {}),
            color: theme.primaryColor,
        }),
        singleValue: (base, props) => {
            return {
                ...base,
                color: props.selectProps.isDisabled ? styles.colors.black300 : theme.primaryColor,
                alignItems: 'center',
                fontFamily: 'NunitoSans',
                fontSize: '14px',
                lineHeight: '30px',
                backgroundColor: 'transparent',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                maxWidth: '100%',
                // the label/value row needs a definite width to be able to ellipsis the value
                ...(fieldLabel ? { width: '100%' } : {})
            }
        }
    }

    if(isMulti) {
        customStyles = {
            ...customStyles,
            valueContainer: (base, props) => {
                return {
                    ...base,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    height: '100%', 
                    padding: '8px 10px'
                }
            }
        }
    }

    if(isMultiRemove) {
        customStyles = {
            ...customStyles,
            multiValue: (styles: any, { data }: any) => {
                return {
                  ...styles,
                  alignItems: 'center',
                  height: '20px',
                  borderRadius: '4px',
                  backgroundColor: data.color
                };
            },
            multiValueLabel: (styles: any, { data }: any) => ({
                ...styles,
                fontSize: '12px',
                lineHeight: '18px',
                fontWeight: 'bold',
                fontFamily: 'Nunito Sans',
                padding: '0px',
                color: 'white'
            }),
            multiValueRemove: (styles: any, { data }: any) => ({
                ...styles,
                height: '20px',
                color: 'white',
            
                ':hover': {
                    backgroundColor: data.color,
                    color: 'white'
                }
            }),
            indicatorsContainer: () => ({
                display: 'none'
            })
        }
    }

    const SingleValue = (props: any) => (
        <component.SingleValue {...props}>
            {props.data.component}
        </component.SingleValue>
    )

    const Option = (props: any) => (
        <component.Option {...props}>
            {props.data.component}
        </component.Option>
    )
    
    const MultiValueRemove = (props: any) => (
        <component.MultiValueRemove {...props}>
            <button onClick={(event: any) => handleMultiValueRemove && handleMultiValueRemove(event, props)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'white' }}>
            &times; {/* or any custom icon */}
            </button>
      </component.MultiValueRemove>
    )

    const Control = (props: any) => (
        <component.Control {...props}>
            {prefixIcon && (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 16, height: 16, marginLeft: 4, color: styles.colors.black300 }}>
                    {prefixIcon}
                </span>
            )}
            {props.children}
        </component.Control>
    )

    const FieldSingleValue = (props: any) => (
        <component.SingleValue {...props}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
                {fieldLabel && <span style={{ flexShrink: 0, fontWeight: 500, color: styles.colors.black600 }}>{fieldLabel}:</span>}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: fieldActive ? styles.colors.primary600 : styles.colors.black300, fontWeight: fieldActive ? 600 : 400 }}>
                    {props.data?.label}
                </span>
            </span>
        </component.SingleValue>
    )

    const DropdownIndicator = (props: any) => (
        <component.DropdownIndicator {...props}>
            <ChevronDown size={14} strokeWidth={2} style={{ color: styles.colors.black300 }} />
        </component.DropdownIndicator>
    )

    return (
        <ReactSelect
            styles={customStyles}
            isMulti={isMulti || isMultiRemove}
            menuIsOpen={isMenuControlled ? menuIsOpen : internalMenuOpen}
            onMenuOpen={openMenuHandler}
            onMenuClose={closeMenuHandler}
            isClearable={isClearable}
            onInputChange={onInputChange}
            components={{
                IndicatorSeparator: () => null,
                DropdownIndicator,
                ...(prefixIcon || fieldLabel ? { Control } : {}),
                ...(fieldLabel ? { SingleValue: FieldSingleValue } : {}),
                ...$components ? {
                    SingleValue: fieldLabel ? FieldSingleValue : SingleValue,
                    Option,
                } : null,

                ...isMultiRemove ? {
                    MultiValueRemove
                } : null,
                ...userComponents,
            }}
            {...props}
        />
    )
}

export default Select